import React from 'react';

import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { getCountColor } from '~/components/SmsVerifyStats/smsVerifyStatsUtils';

import CsDashboardCsvExport from './CsDashboardCsvExport';
import { formatJsonDate } from './csDashboardUtils';
import { COLOR_WHITE, CS_FEATURE_MAP } from './features/registry';
import type { MergedAgentRow } from './types';

type Props = {
  visibleRows: MergedAgentRow[];
  selectedFeatureIds: string[];
  allDates: string[];
  maxCellCount: number;
};

export default function CsDashboardAgentTable({ visibleRows, selectedFeatureIds, allDates, maxCellCount }: Props) {
  return (
    <Box mt={4} mb={6}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Per-agent daily counts</Typography>
          <CsDashboardCsvExport
            rows={visibleRows}
            featureIds={selectedFeatureIds}
            featureMap={CS_FEATURE_MAP}
            dates={allDates}
          />
        </Stack>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              {/* Row 1: feature group headers spanning their date + total columns */}
              <TableRow>
                <TableCell rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Agent
                  </Typography>
                </TableCell>
                {selectedFeatureIds.map((featureId) => {
                  const feature = CS_FEATURE_MAP[featureId];
                  return (
                    <TableCell
                      key={`group-${featureId}`}
                      align="center"
                      colSpan={allDates.length + 1}
                      sx={{ borderLeft: '2px solid', borderLeftColor: 'divider' }}
                    >
                      <Chip
                        label={feature?.label ?? featureId}
                        size="small"
                        sx={{ bgcolor: feature?.color ?? '#ccc', color: COLOR_WHITE }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
              {/* Row 2: date + Total sub-columns under each feature group */}
              <TableRow>
                {selectedFeatureIds.flatMap((featureId) => [
                  ...allDates.map((date, idx) => (
                    <TableCell
                      key={`${featureId}-h-${date}`}
                      align="center"
                      sx={{
                        whiteSpace: 'nowrap',
                        ...(idx === 0 ? { borderLeft: '2px solid', borderLeftColor: 'divider' } : {}),
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                        {formatJsonDate(date)}
                      </Typography>
                    </TableCell>
                  )),
                  <TableCell key={`${featureId}-h-total`} align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption" fontWeight="bold">
                      Total
                    </Typography>
                  </TableCell>,
                ])}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.agentId} hover>
                  <TableCell>
                    <Typography variant="body2">{row.agentEmail ?? row.agentId}</Typography>
                  </TableCell>
                  {selectedFeatureIds.flatMap((featureId) => {
                    const byDate = row.countsByFeatureAndDate[featureId] ?? {};
                    const total = allDates.reduce((sum, date) => sum + (byDate[date] ?? 0), 0);
                    return [
                      ...allDates.map((date, idx) => {
                        const count = byDate[date] ?? 0;
                        return (
                          <TableCell
                            key={`${row.agentId}-${featureId}-${date}`}
                            align="center"
                            sx={{
                              backgroundColor: getCountColor(count, maxCellCount),
                              ...(idx === 0 ? { borderLeft: '2px solid', borderLeftColor: 'divider' } : {}),
                            }}
                          >
                            <Typography variant="body2">{count}</Typography>
                          </TableCell>
                        );
                      }),
                      <TableCell key={`${row.agentId}-${featureId}-total`} align="center">
                        <Typography variant="body2" fontWeight="bold">
                          {total}
                        </Typography>
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
